class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll('#cart-icon-bubble, #cart-icon-bubble-desktop');

    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Free-delivery meter. The drawer is re-rendered from the server on every cart change,
// so each render inserts a fresh element; animate it from the value the previous
// element showed instead of jumping.
let cartMeterLastProgress = null;

class CartShippingMeter extends HTMLElement {
  connectedCallback() {
    const target = parseFloat(this.dataset.progress) || 0;
    const from = cartMeterLastProgress;
    cartMeterLastProgress = target;

    const fill = this.querySelector('.cart-meter__fill');
    if (!fill || from === null || from === target) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const unlocking = from < 1 && target >= 1;
    if (unlocking) this.classList.remove('cart-meter--done');

    fill.style.transition = 'none';
    fill.style.transform = `scaleX(${from})`;
    fill.getBoundingClientRect();
    fill.style.transition = '';

    requestAnimationFrame(() => {
      fill.style.transform = `scaleX(${target})`;
      if (unlocking) this.classList.add('cart-meter--done');
    });
  }
}

if (!customElements.get('cart-shipping-meter')) customElements.define('cart-shipping-meter', CartShippingMeter);

// Next-day delivery line, worded like Runlimited's delivery promise. Used in the cart
// drawer (snippets/cart-shipping-meter.liquid) and under the PDP Add to bag button
// (snippets/pdp-next-day.liquid). Decided in the browser, because both can be served
// from Shopify's page cache, from UK time and the next-day theme settings:
//   - inside the window (an allowed weekday, before the cutoff, neither today nor
//     tomorrow a bank holiday): "Need them tomorrow? Order within 3h 12m and select
//     Next Day Delivery."
//   - outside it: "Need them by Tuesday? Order now and select Next Day Delivery."
//     (an order now goes out on the next slot day and lands the day after).
// In the drawer the free-delivery meter only shows if no slot can be found (e.g. no
// days set). Re-checks every 30 seconds while in the page.
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

class CartDeliveryMessage extends HTMLElement {
  connectedCallback() {
    this.line = this.querySelector('[data-nextday-line]');
    this.meter = this.querySelector('cart-shipping-meter');
    this.recovery = this.querySelector('[data-bundle-recovery]');
    if (!this.line && !this.recovery) return;

    this.cutoffHour = parseInt(this.dataset.cutoffHour, 10);
    this.days = (this.dataset.days || '').toLowerCase().split(',').filter(Boolean);
    this.holidays = (this.dataset.holidays || '').split(',').filter(Boolean);

    this.update();
    this.timer = setInterval(() => this.update(), 30000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  update() {
    if (this.showRecovery()) return;
    if (!this.line || Number.isNaN(this.cutoffHour)) return;
    const html = this.message(this.ukNow());

    this.line.hidden = !html;
    if (html && this.line.innerHTML !== html) this.line.innerHTML = html;
    if (this.meter) this.meter.hidden = Boolean(html);
    // The PDP element itself starts hidden; the drawer wrapper also holds the meter.
    if (!this.meter) this.hidden = !html;
  }

  // Bundle recovery line (snippets/bundle-recovery.liquid): while its offer is in its date
  // window it takes the delivery slot, replacing the next-day line and the meter.
  showRecovery() {
    const line = this.recovery;
    if (!line) return false;
    const on = witBundleInWindow(line);
    line.hidden = !on;
    if (on) {
      if (this.line) this.line.hidden = true;
      if (this.meter) this.meter.hidden = true;
      this.hidden = false;
    } else if (!this.line && !this.meter) {
      this.hidden = true;
    } else if (this.meter && !this.line) {
      this.meter.hidden = false;
    }
    return on;
  }

  message(now) {
    const pronoun = this.dataset.pronoun || 'it';
    const option = `Next Day Delivery${this.dataset.price ? ` (${this.escape(this.dataset.price)})` : ''}`;
    const remaining = this.isSlotDay(now.date) ? this.cutoffHour * 3600 - now.seconds : 0;

    if (remaining > 0) {
      return `<strong>Need ${pronoun} tomorrow?</strong> Order within <strong>${this.formatRemaining(remaining)}</strong> and select ${option}.`;
    }
    const slot = this.findNextSlot(now.date);
    if (!slot) return '';
    const day = this.dayLabel(now.date, this.addDays(slot, 1));
    return `<strong>Need ${pronoun} by ${day}?</strong> Order now and select ${option}.`;
  }

  // Current UK date (as a UTC-midnight Date, for day arithmetic) and seconds since midnight.
  ukNow() {
    const parts = {};
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(new Date())
      .forEach((part) => (parts[part.type] = part.value));
    return {
      date: new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day))),
      seconds: Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second),
    };
  }

  addDays(date, n) {
    return new Date(date.getTime() + n * 86400000);
  }

  iso(date) {
    return date.toISOString().slice(0, 10);
  }

  // A day you can order on for delivery the next day.
  isSlotDay(date) {
    return (
      this.days.includes(WEEKDAYS[date.getUTCDay()].toLowerCase()) &&
      !this.holidays.includes(this.iso(date)) &&
      !this.holidays.includes(this.iso(this.addDays(date, 1)))
    );
  }

  findNextSlot(today) {
    for (let n = 1; n <= 21; n++) {
      const day = this.addDays(today, n);
      if (this.isSlotDay(day)) return day;
    }
    return null;
  }

  // Delivery day after the cutoff: always at least two days out, so never "tomorrow".
  dayLabel(today, date) {
    const diff = Math.round((date - today) / 86400000);
    if (diff < 7) return WEEKDAY_NAMES[date.getUTCDay()];
    return `${WEEKDAYS[date.getUTCDay()]} ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
  }

  formatRemaining(seconds) {
    const minutes = Math.max(1, Math.ceil(seconds / 60));
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m}m`;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  }

  escape(text) {
    return text.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  }
}

if (!customElements.get('cart-delivery-message')) customElements.define('cart-delivery-message', CartDeliveryMessage);
if (!customElements.get('pdp-delivery-message')) {
  customElements.define('pdp-delivery-message', class PdpDeliveryMessage extends CartDeliveryMessage {});
}

// Bundle offers. <bundle-pill> (snippets/bundle-pill.liquid) is a link to the offer's
// collection; with <dialog> support it opens the builder instead. Intent (pointer, touch,
// focus) loads assets/bundle-builder.js + its CSS and prefetches the range sections and
// /cart.js, so the builder is usually ready before the tap lands. Dates are re-checked
// here because the PDP can come from Shopify's page cache (seconds since the epoch).
function witBundleInWindow(el) {
  const now = Date.now() / 1000;
  const { start, end } = el.dataset;
  return !((start && start > now) || (end && end <= now));
}

function witBundleTrack(name, payload) {
  try {
    window.Shopify?.analytics?.publish(`wit_bundle_${name}`, payload);
  } catch (e) {}
  try {
    window.clarity?.('event', `wit_bundle_${name}`);
  } catch (e) {}
}

const witBundleCache = new Map();

class BundlePill extends HTMLElement {
  connectedCallback() {
    // Dawn's variant swap re-inserts this HTML, and inline scripts don't re-run then.
    this.hidden = !witBundleInWindow(this);
    this.link = this.querySelector('a');
    if (!this.link || !window.HTMLDialogElement?.prototype.showModal || this.ready) return;
    this.ready = true;
    this.link.setAttribute('aria-haspopup', 'dialog');
    const prime = () => this.prime();
    this.addEventListener('pointerenter', prime, { once: true });
    this.addEventListener('focusin', prime, { once: true });
    this.addEventListener('touchstart', prime, { once: true, passive: true });
    this.link.addEventListener('click', (event) => {
      event.preventDefault();
      // detail is 0 for Enter/Space: only keyboard opens move focus visibly (iOS rings any scripted focus)
      this.open(!event.detail);
    });
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          io.disconnect();
          witBundleTrack('pill_view', { offer: this.dataset.offer, product_id: this.config.viewed.id });
        },
        { threshold: 0.5 }
      );
      io.observe(this);
    }
    // Drawer recovery link from another page: /products/x#bundle
    if (location.hash === '#bundle' && !this.hidden) {
      history.replaceState(null, '', location.pathname + location.search);
      if (document.readyState === 'complete') this.open();
      else window.addEventListener('load', () => this.open(), { once: true });
    }
  }

  get config() {
    return (this._config ||= JSON.parse(this.querySelector('[data-bundle-config]').textContent));
  }

  prime() {
    if (this.primed) return this.primed;
    const range = (url) => {
      if (!witBundleCache.has(url)) {
        witBundleCache.set(url, fetch(url).then((r) => (r.ok ? r.text() : null)).catch(() => null));
      }
    };
    this.config.groups.forEach((group) => range(`${group.url}?section_id=bundle-builder`));
    witBundleCache.cart = fetch(`${routes.cart_url}.js`).then((r) => r.json());
    witBundleCache.cart.catch(() => {});

    const js = this.dataset.js;
    const css = this.dataset.css;
    const load = (tag, attrs) =>
      new Promise((resolve, reject) => {
        const el = Object.assign(document.createElement(tag), attrs, { onload: resolve, onerror: reject });
        document.head.append(el);
      });
    const style = load('link', { rel: 'stylesheet', href: css }).catch(() => {});
    this.primed = Promise.all([
      Promise.race([style, new Promise((resolve) => setTimeout(resolve, 1500))]),
      customElements.get('bundle-builder') ? null : load('script', { src: js }),
    ]);
    this.primed.catch(() => (this.primed = null));
    return this.primed;
  }

  async open(kbd) {
    try {
      await this.prime();
      await customElements.whenDefined('bundle-builder');
    } catch (e) {
      window.location.href = this.link.href;
      return;
    }
    let builder = document.querySelector('bundle-builder');
    if (!builder) builder = document.body.appendChild(document.createElement('bundle-builder'));
    builder.open({ config: this.config, pill: this, cache: witBundleCache, kbd });
  }
}

if (!customElements.get('bundle-pill')) customElements.define('bundle-pill', BundlePill);

// Recovery link in the drawer: open the builder here if this PDP shows that offer's pill,
// otherwise follow the link to the product with #bundle.
document.addEventListener('click', (event) => {
  const link = event.target.closest?.('[data-bundle-open]');
  if (!link) return;
  const pill = [...document.querySelectorAll('bundle-pill')].find(
    (el) => el.dataset.offer === link.dataset.bundleOpen && el.ready && !el.hidden && !el.closest('quick-add-modal')
  );
  if (!pill) return;
  event.preventDefault();
  document.querySelector('cart-drawer')?.close();
  pill.open();
});

// Drawer recommendation card (snippets/cart-drawer-recs.liquid). Ticking the checkbox
// adds the product, as in the Essential Upsell card it replaces, but a sized product
// is never added without a chosen size.
class CartRecForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.checkbox = this.querySelector('.cart-rec__check');
    this.sizeSelect = this.querySelector('select[name="id"]');
    this.errorEl = this.querySelector('.cart-rec__error');

    // <cart-drawer-items> treats any bubbling change event as a quantity edit.
    this.addEventListener('change', (event) => {
      event.stopPropagation();
      this.errorEl.textContent = '';
      if (event.target === this.checkbox && this.checkbox.checked) this.add();
    });
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.checkbox.checked = true;
      this.add();
    });
  }

  add() {
    if (this.busy) return;

    if (this.sizeSelect && !this.sizeSelect.value) {
      this.checkbox.checked = false;
      this.errorEl.textContent = window.witStrings.cartRecChooseSize;
      this.sizeSelect.focus();
      return;
    }

    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return this.form.submit();

    this.busy = true;
    this.checkbox.disabled = true;
    this.classList.add('is-loading');
    this.form.setAttribute('aria-busy', 'true');
    const liveRegion = document.getElementById('CartDrawer-LiveRegionText');
    if (liveRegion) liveRegion.textContent = window.witStrings.cartRecAdding.replace('[title]', () => this.dataset.title);

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(this.form);
    formData.append(
      'sections',
      drawer.getSectionsToRender().map((section) => section.id)
    );
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    const title = this.dataset.title;
    const closer = this.dataset.closer === 'true';
    const variantId = formData.get('id');

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.fail(response.description || window.cartStrings.error);
          return;
        }

        drawer.renderContents(response);

        // The rendered drawer replaces this element, so focus and announce on the new one.
        setTimeout(() => {
          drawer.querySelector('.drawer__inner')?.focus();
          const status = document.getElementById('CartDrawer-LiveRegionText');
          const deliveryText =
            document.querySelector('#CartDrawer-NextDayText:not([hidden])') ||
            document.querySelector('cart-shipping-meter:not([hidden]) #CartDrawer-MeterText');
          if (status) status.textContent = `${window.witStrings.cartRecAdded.replace('[title]', () => title)} ${deliveryText ? deliveryText.textContent.trim().replace(/\s+/g, ' ') : ''}`;
        }, 100);

        try {
          window.Shopify?.analytics?.publish('cart_drawer_rec_add', { variantId, closer });
        } catch (e) {}
      })
      .catch(() => this.fail(window.cartStrings.error));
  }

  fail(message) {
    this.busy = false;
    this.classList.remove('is-loading');
    this.form.removeAttribute('aria-busy');
    this.checkbox.disabled = false;
    this.checkbox.checked = false;
    this.errorEl.textContent = message;
  }
}

// One card at a time with previous/next arrows; swipe/scroll also works.
class CartRecsSlider extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.cart-recs__list');
    this.nav = this.querySelector('.cart-recs__nav');
    if (!this.list || !this.nav || this.list.children.length < 2) return;

    this.prev = this.nav.querySelector('[data-step="-1"]');
    this.next = this.nav.querySelector('[data-step="1"]');
    this.nav.hidden = false;
    this.nav.addEventListener('click', (event) => {
      const button = event.target.closest('[data-step]');
      if (!button) return;
      this.list.scrollBy({ left: Number(button.dataset.step) * this.list.clientWidth, behavior: 'smooth' });
    });
    this.list.addEventListener('scroll', () => this.updateArrows(), { passive: true });
    this.updateArrows();
  }

  updateArrows() {
    const max = this.list.scrollWidth - this.list.clientWidth - 2;
    this.prev.disabled = this.list.scrollLeft <= 2;
    this.next.disabled = this.list.scrollLeft >= max;
  }
}

if (!customElements.get('cart-recs-slider')) customElements.define('cart-recs-slider', CartRecsSlider);

if (!customElements.get('cart-rec-form')) customElements.define('cart-rec-form', CartRecForm);
